import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiMinimize2, FiMaximize2 } from 'react-icons/fi';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { SkeletonTable } from './Skeleton';

function resolveAccessor(row, accessor) {
  if (typeof accessor === 'function') return accessor(row);
  if (!accessor) return undefined;
  return row?.[accessor];
}

function sortRows(rows, column, direction) {
  if (!column) return rows;
  const sortBy = column.sortBy || column.accessor;
  if (!sortBy) return rows;
  const dir = direction === 'desc' ? -1 : 1;
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = resolveAccessor(a, sortBy);
    const bv = resolveAccessor(b, sortBy);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    // ISO date string compare
    const ad = typeof av === 'string' ? Date.parse(av) : NaN;
    const bd = typeof bv === 'string' ? Date.parse(bv) : NaN;
    if (!Number.isNaN(ad) && !Number.isNaN(bd)) return (ad - bd) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
  return copy;
}

/**
 * DataTable — advanced list table.
 *
 *   <DataTable
 *     columns={[
 *       { id: 'name', header: 'Name', accessor: 'Nom', sortable: true,
 *         cell: (row) => <span className="font-medium">{row.Nom}</span> },
 *       { id: 'key',  header: 'Key',  accessor: 'Key', mono: true, sortable: true },
 *       { id: 'end',  header: 'Ends', accessor: 'endDate',
 *         cell: (row) => <ExpiryCell date={row.endDate}/>, sortable: true },
 *       { id: 'a', header: '', align: 'right', width: 80,
 *         cell: (row) => <RowActions row={row}/> },
 *     ]}
 *     data={clients}
 *     rowKey="id"
 *     onRowClick={(row) => openDrawer(row)}
 *     loading={loading}
 *     empty={<EmptyState.../>}
 *     emptyFiltered={<EmptyState.../>}
 *     isFiltered={hasQuery || hasStatus}
 *     initialSort={{ columnId: 'name', direction: 'asc' }}
 *     pageSize={25}
 *   />
 */
export function DataTable({
  columns,
  data,
  rowKey,
  onRowClick,
  loading,
  empty,
  emptyFiltered,
  isFiltered,
  initialSort,
  pageSize,
  density: densityProp,
  onDensityChange,
  className,
}) {
  const [internalDensity, setInternalDensity] = useState('comfortable');
  const density = densityProp ?? internalDensity;
  const setDensity = onDensityChange ?? setInternalDensity;

  const [sort, setSort] = useState(initialSort || null);
  const [page, setPage] = useState(0);

  const getRowKey = (row, i) => {
    if (typeof rowKey === 'function') return rowKey(row, i);
    if (typeof rowKey === 'string') return row?.[rowKey];
    return i;
  };

  const sortedRows = useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.id === sort.columnId);
    return sortRows(data, col, sort.direction);
  }, [data, sort, columns]);

  const total = sortedRows.length;
  const pageCount = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const clampedPage = Math.min(page, pageCount - 1);
  const visibleRows = pageSize
    ? sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize)
    : sortedRows;

  const handleSort = (col) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (!prev || prev.columnId !== col.id) return { columnId: col.id, direction: 'asc' };
      if (prev.direction === 'asc') return { columnId: col.id, direction: 'desc' };
      return null; // third click clears sort
    });
    setPage(0);
  };

  const rowPadY = density === 'compact' ? 'py-1.5' : 'py-2.5';
  const rowHeight = density === 'compact' ? 'h-9' : 'h-11';

  if (loading) {
    return (
      <div className={className}>
        <SkeletonTable rows={8} columns={columns.length} />
      </div>
    );
  }

  if (total === 0) {
    const state = isFiltered ? emptyFiltered || empty : empty;
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface shadow-e1',
          className,
        )}
      >
        {state}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="relative overflow-hidden rounded-lg border border-line bg-surface shadow-e1">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-body-sm">
            <thead>
              <tr>
                {columns.map((col) => {
                  const isSorted = sort?.columnId === col.id;
                  const sortable = Boolean(col.sortable);
                  return (
                    <th
                      key={col.id}
                      scope="col"
                      style={col.width ? { width: col.width } : undefined}
                      className={cn(
                        'sticky top-0 z-10 bg-surface-muted',
                        'border-b border-line',
                        'px-3 py-2.5',
                        'text-micro text-fg-muted',
                        'whitespace-nowrap',
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center',
                        !col.align && 'text-left',
                        col.headerClassName,
                      )}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col)}
                          className={cn(
                            'group inline-flex items-center gap-1 transition-colors',
                            'hover:text-fg',
                            isSorted && 'text-fg',
                          )}
                        >
                          {col.header}
                          <span
                            className={cn(
                              'inline-flex flex-col leading-none',
                              isSorted ? 'opacity-100' : 'opacity-40 group-hover:opacity-100',
                            )}
                          >
                            <FiChevronUp
                              size={10}
                              className={cn(
                                '-mb-[2px]',
                                isSorted && sort.direction === 'asc'
                                  ? 'text-brand-600 dark:text-brand-400'
                                  : 'text-fg-subtle',
                              )}
                            />
                            <FiChevronDown
                              size={10}
                              className={cn(
                                isSorted && sort.direction === 'desc'
                                  ? 'text-brand-600 dark:text-brand-400'
                                  : 'text-fg-subtle',
                              )}
                            />
                          </span>
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, i) => {
                const key = getRowKey(row, i);
                const clickable = Boolean(onRowClick);
                return (
                  <tr
                    key={key}
                    onClick={clickable ? () => onRowClick(row) : undefined}
                    className={cn(
                      'group transition-colors',
                      rowHeight,
                      clickable && 'cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-800',
                    )}
                  >
                    {columns.map((col, ci) => {
                      const content = col.cell
                        ? col.cell(row)
                        : resolveAccessor(row, col.accessor);
                      return (
                        <td
                          key={col.id}
                          className={cn(
                            'border-b border-line/70 px-3',
                            rowPadY,
                            'text-fg align-middle',
                            col.mono && 'font-mono text-caption text-fg-muted',
                            col.align === 'right' && 'text-right',
                            col.align === 'center' && 'text-center',
                            ci === 0 && 'pl-4',
                            ci === columns.length - 1 && 'pr-4',
                            col.className,
                          )}
                          onClick={(e) => {
                            if (col.stopRowClick) e.stopPropagation();
                          }}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pageSize && pageCount > 1 && (
          <div className="flex items-center justify-between border-t border-line bg-surface-muted px-4 py-2">
            <p className="text-caption text-fg-subtle">
              Showing {clampedPage * pageSize + 1}–{Math.min((clampedPage + 1) * pageSize, total)}{' '}
              of {total}
            </p>
            <div className="flex items-center gap-1">
              <IconButton
                variant="ghost"
                size="sm"
                tooltip="Previous page"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={clampedPage === 0}
              >
                <FiChevronLeft />
              </IconButton>
              <span className="px-2 text-caption text-fg-muted">
                {clampedPage + 1} / {pageCount}
              </span>
              <IconButton
                variant="ghost"
                size="sm"
                tooltip="Next page"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={clampedPage >= pageCount - 1}
              >
                <FiChevronRight />
              </IconButton>
            </div>
          </div>
        )}
      </div>

      {/* Meta + density control */}
      <div className="flex items-center justify-between px-1">
        <p className="text-caption text-fg-subtle">
          {total} {total === 1 ? 'row' : 'rows'}
        </p>
        <div className="flex items-center gap-1">
          <IconButton
            variant="ghost"
            size="sm"
            tooltip={density === 'compact' ? 'Comfortable density' : 'Compact density'}
            onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
          >
            {density === 'compact' ? <FiMaximize2 /> : <FiMinimize2 />}
          </IconButton>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
